//@ts-check

import React from "react"

// Material
import { makeStyles, withStyles } from "@material-ui/core/styles"
import classnames from "classnames"
import TabWrapper from "./tab-wrapper"
import Tab from "@material-ui/core/Tab"

import SnippetPopover from "./snippet-popover"
const CONSTS = require("shared/consts")
const formatFilename = require("../utils/formats").formatFilename
import Tooltip from "./tooltip"
import { track } from "../utils/tracking"

const processHighlightData = groupByFile => {
  const processedData = {}
  groupByFile.forEach(errorItem => {
    const { filePath = "" } = errorItem
    const filePathSplit = filePath.split("/")
    const numberOfPathParts = filePathSplit.length
    const fileName = filePathSplit[numberOfPathParts - 1]
    if (!processedData[filePath]) {
      processedData[filePath] = []
    }
    processedData[filePath].push({ ...errorItem, fileName })
  })
  return processedData
}

const CODE_ROW_HEIGHT = 35
const CODE_SECTION_PADDING = 16

/**
 * @param props {{
 * snippets,
 * onViewSourceClick,
 * }}
 */
const SnippetSection = function({ snippets = [], onViewSourceClick }) {
  const styles = makeStyles(theme => ({
    snippetSection: {
      marginTop: theme.spacing(2),
    },
    snippetCode: {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      border: "1px solid #ddd",
    },
    snippetRow: {
      cursor: "pointer",
      "&:nth-child(odd)": {
        backgroundColor: "#f8f9fa",
      },
      "&:nth-child(even)": {
        backgroundColor: "#dee6ec",
      },
      "&:hover": {
        filter: "brightness(95%)",
      },
    },
    codeSection: {
      display: "block",
      backgroundColor: "#EAEAEE",
      padding: CODE_SECTION_PADDING,
      transition: "height 0.5s",
      overflow: "hidden",
    },
    codeTable: {
      borderCollapse: "collapse",
      transition: "maxHeight 0.5s",
      fontSize: "0.75rem", // = 12px (otherwise `font-size` can occasionally and instantaneously be reset or overridden)
    },
    lineNumberColumn: {
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      fontWeight: "bold",
      textAlign: "end",
    },
    sourceCodeColumn: {
      width: "100%",
      paddingLeft: theme.spacing(1),
    },
    highlightedLine: {
      backgroundColor: "rgba(255, 0, 0, 0.2)",
    },
    fileLocation: {
      marginRight: theme.spacing(1),
    },
  }))

  const StyledTab = withStyles({
    root: {
      maxWidth: `${CONSTS.SNIPPET_FILENAME_LENGTH_DISPLAYED}ch`,
      minWidth: 20,
    },
  })(Tab)

  const classes = styles({})

  const [value, setValue] = React.useState(0)

  const [anchorEl, setAnchorEl] = React.useState(null)

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const groupedSnippets = processHighlightData(snippets)

  const snippetData = Object.entries(groupedSnippets)

  const selectedTab = snippetData[value] || []
  const selectedTabData = selectedTab[1] || []
  const selectedTabItemsCount = selectedTabData.length || 0

  const handleClick = ({event, filePath, lineNumber}) => {
    setAnchorEl(event.currentTarget)
    track("View test fail highlighting", { filePath, lineNumber })
    console.log(filePath, lineNumber)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  if (snippetData.length === 0) {
    return null
  }
  let isMatchingFileNames = false
  const firstFileName = snippetData[0][0]
  if (snippetData.length > 1 && firstFileName) {
    isMatchingFileNames = true
  }

  return (
    <div className={classes.snippetSection}>
      {isMatchingFileNames && (
        <TabWrapper
          handleChange={handleChange}
          selectedCriterion={value}
          tabIndicatorHeight={4}
          marginBottom={0}
        >
          {snippetData.map(([_key, fileErrors]) => {
            if (!fileErrors || fileErrors.length === 0) {
              return null
            }
            const { fileName } = fileErrors[0]
            const formattedFileName = formatFilename(
              fileName,
              CONSTS.SNIPPET_FILENAME_LENGTH_DISPLAYED,
            )
            const isFileNameShortened = formattedFileName !== fileName
            return (
              <StyledTab
                label={
                  <Tooltip tip={isFileNameShortened && fileName}>
                    {formattedFileName}
                  </Tooltip>
                }
              />
            )
          })}
        </TabWrapper>
      )}
      <code
        className={classes.codeSection}
        style={{
          height:
            selectedTabItemsCount * CODE_ROW_HEIGHT + 2 * CODE_SECTION_PADDING,
        }}
      >
        <table
          className={classes.codeTable}
          style={{ maxHeight: selectedTabItemsCount * CODE_ROW_HEIGHT }}
        >
          {selectedTabData.map(
            ({ lines, highlightIndex, fileName, filePath }) => {
              const linesToHighlight = lines.filter(
                ([lineNumb]) => lineNumb === highlightIndex,
              )
              if (linesToHighlight.length === 0) {
                return null
              }
              const [lineNumber, lineCode] = linesToHighlight[0]
              const itemId = `error-${filePath}#${highlightIndex}`
              return (
                <>
                  <tr
                    id={itemId}
                    className={classes.snippetRow}
                    key={`item-${itemId}`}
                    onClick={event => handleClick({event, filePath, lineNumber})}
                    style={{ height: CODE_ROW_HEIGHT }}
                  >
                    <td
                      className={classnames([
                        classes.lineNumberColumn,
                        classes.snippetCode,
                      ])}
                    >
                      {lineNumber}
                    </td>
                    <td
                      className={classnames([
                        classes.sourceCodeColumn,
                        classes.snippetCode,
                      ])}
                    >
                      {lineCode}
                    </td>
                  </tr>
                  <SnippetPopover
                    onViewSourceClick={onViewSourceClick}
                    filePath={filePath}
                    highlightIndex={highlightIndex}
                    itemId={itemId}
                    lines={lines}
                    anchorEl={anchorEl}
                    handleClose={handleClose}
                  />
                </>
              )
            },
          )}
        </table>
      </code>
    </div>
  )
}

export default React.memo(SnippetSection)
